/**
 * ContractsPage — VEN-08: Contratos comerciales
 */
import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { fmtDate } from '@/lib/utils'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import { FileText, Plus, Loader2, XCircle, Pencil, RefreshCw, Search } from 'lucide-react'
import {
  useContracts,
  useCancelContract,
  useRenewContract,
  type Contract,
  type ContractStatus,
} from '../hooks/useSales'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { ContractModal } from './ContractModal'
import { DataTable } from '@/components/ds/DataTable'
import { KebabMenu } from '@/components/KebabMenu'
import { Pagination } from '@/components/ds/Pagination'

const STATUS_CONFIG: Record<ContractStatus, { label: string; cls: string; dotCls: string }> = {
  draft:     { label: 'Borrador',  cls: 'bg-surface-muted text-content-muted',                                            dotCls: 'bg-content-subtle' },
  active:    { label: 'Vigente',   cls: 'bg-success-surface text-success-content',    dotCls: 'bg-emerald-400' },
  expired:   { label: 'Vencido',   cls: 'bg-warning-surface text-warning-content',            dotCls: 'bg-warning-solid' },
  cancelled: { label: 'Anulado',   cls: 'bg-surface-muted text-content-muted',                                            dotCls: 'bg-content-subtle' },
  renewed:   { label: 'Renovado',  cls: 'bg-primary-50 text-primary-hover',                                               dotCls: 'bg-primary' },
}

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'active', label: 'Vigente' },
  { value: 'expired', label: 'Vencido' },
  { value: 'renewed', label: 'Renovado' },
  { value: 'cancelled', label: 'Anulado' },
]

function StatusBadge({ status }: { status: ContractStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.active
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

function fmtValue(value: string | null, currency: string) {
  if (!value) return '—'
  const num = parseFloat(value)
  if (currency === 'CLP') {
    return formatMoneyForTenant(num)
  }
  return `${num.toLocaleString('es-CL', { maximumFractionDigits: 2 })} ${currency}`
}

export function ContractsPage() {
  const locale = useTenantLocale()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Contract | null>(null)

  const { data, isLoading } = useContracts({
    status: statusFilter || undefined,
    search: search || undefined,
    page,
    page_size: 25,
  })

  const cancel = useCancelContract()
  const renew = useRenewContract()
  const { confirm: confirmAction, modalProps } = useConfirmModal()

  const openEdit = (c: Contract) => { setEditing(c); setModalOpen(true) }
  const openNew = () => { setEditing(null); setModalOpen(true) }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Contratos"
        subtitle={`${data?.count ?? 0} contrato${data?.count !== 1 ? 's' : ''}`}
        help={HELP['sales.contracts']}
      >
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover transition-colors">
          <Plus size={15} />Nuevo Contrato
        </button>
      </PageHeader>

      {/* Filtros + búsqueda */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar por número, título, cliente, OC"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Buscar por número, título, cliente, OC..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-divider rounded-lg bg-surface text-content focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map(s => (
            <button key={s.value} onClick={() => { setStatusFilter(s.value); setPage(1) }}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors
                ${statusFilter === s.value
                  ? 'bg-primary text-on-primary border-primary'
                  : 'border-divider text-content-muted hover:bg-soft'}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-content-subtle">
            <Loader2 size={20} className="animate-spin mr-2" />Cargando...
          </div>
        ) : !data?.contracts.length ? (
          <div className="text-center py-16">
            <FileText size={32} className="mx-auto text-content-subtle mb-3" />
            <p className="text-content-muted text-sm font-medium">Sin contratos</p>
            <p className="text-content-subtle text-xs mt-1">Crea un contrato para enmarcar la facturación de un cliente.</p>
            <button
              onClick={openNew}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50">
              <Plus size={14} />Crear primer contrato
            </button>
          </div>
        ) : (
          <DataTable<Contract>
            columns={[
              {
                key: 'numero', header: 'N° / Título',
                render: c => (
                  <>
                    <div className="text-sm font-medium text-content">{c.contract_number}</div>
                    {c.title && <div className="text-xs text-content-subtle max-w-[200px] truncate">{c.title}</div>}
                  </>
                ),
              },
              {
                key: 'cliente', header: 'Cliente',
                render: c => <div className="max-w-[160px] truncate">{c.customer_name}</div>,
              },
              { key: 'vendedor', header: 'Vendedor', render: c => c.seller_name || '—' },
              {
                key: 'vigencia', header: 'Vigencia',
                render: c => (
                  <span className="whitespace-nowrap">
                    {fmtDate(c.start_date, locale)} → {c.end_date ? fmtDate(c.end_date, locale) : '∞'}
                  </span>
                ),
              },
              {
                key: 'valor', header: 'Valor', numeric: true,
                render: c => <span className="font-medium text-content">{fmtValue(c.contract_value, c.currency)}</span>,
              },
              {
                key: 'docs', header: 'Docs.',
                // La celda tiene su propia acción: el clic no debe abrir la fila.
                render: c => (
                  <div className="text-center" onClick={e => e.stopPropagation()}>
                    {(c.documents_count ?? 0) > 0 ? (
                      <button
                        onClick={() => navigate(`/sales/documents?contract=${c.id}`)}
                        className="text-primary hover:underline text-sm font-medium"
                        title="Ver documentos de este contrato">
                        {c.documents_count}
                      </button>
                    ) : (
                      <span className="text-content-subtle text-sm">0</span>
                    )}
                  </div>
                ),
              },
              { key: 'estado', header: 'Estado', render: c => <StatusBadge status={c.status} /> },
              {
                key: 'acciones', header: 'Acciones', numeric: true,
                render: c => (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    {c.status !== 'cancelled' && (
                      <button
                        onClick={() => openEdit(c)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft">
                        <Pencil size={10} />Editar
                      </button>
                    )}
                    {(c.status === 'active' || c.status === 'expired') && (
                      <button
                        onClick={() => {
                          confirmAction({
                            message: `Se creará un nuevo contrato a partir de ${c.contract_number}, que quedará marcado como renovado. Luego podrás ajustar la vigencia del nuevo contrato. ¿Continuar?`,
                            onConfirm: () => {
                              renew.mutate({ id: c.id }, {
                                onSuccess: (nc: Contract) => { setEditing(nc); setModalOpen(true) },
                                onError: (err: any) => alert(err?.response?.data?.error?.message || 'No se pudo renovar.'),
                              })
                            },
                          })
                        }}
                        disabled={renew.isPending}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary border border-primary/40 rounded-lg hover:bg-primary/10 disabled:opacity-60">
                        <RefreshCw size={10} />Renovar
                      </button>
                    )}
                    <KebabMenu items={[
                      {
                        label: 'Anular',
                        icon: <XCircle size={14} />,
                        onClick: () => confirmAction({
                          message: '¿Anular este contrato? Las suscripciones activas ligadas se pausarán.',
                          variant: 'danger',
                          onConfirm: () => cancel.mutate(c.id),
                        }),
                        hidden: c.status === 'cancelled',
                        className: 'text-danger-content-soft hover:bg-danger-surface',
                        disabled: cancel.isPending,
                      },
                    ]} />
                  </div>
                ),
              },
            ]}
            rows={data.contracts}
            rowKey={c => c.id}
            onRowClick={openEdit}
            minWidth={900}
            caption="Contratos"
          />
        )}
      </div>

      {/* Paginación */}
      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data?.count ?? 0}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      {/* Modal */}
      {modalOpen && (
        <ContractModal
          contract={editing}
          onClose={() => { setModalOpen(false); setEditing(null) }}
        />
      )}

      <ConfirmModal {...modalProps} />
    </div>
  )
}
